/*************************************************************************
 * DocumentScanner (React) – JScanify/OpenCV.js-Variante
 * ----------------------------------------------------
 * - ersetzt TensorFlow-Pipeline komplett
 * - nutzt OpenCV.js (WASM) + jscanify für Dokumenterkennung & Warp
 * - UI-/State-Flow bleibt unverändert (Countdown, Fade, Callback etc.)
 * - OpenCV.js muss EINMAL in public/index.html eingebunden sein:
 *     <script async src="https://docs.opencv.org/4.9.0/opencv.js"></script>
 *************************************************************************/

import React, { useState, useRef, useEffect } from 'react';
/* global jscanify, cv */      // <-- ESLint Hint
import { Box, CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

// -------- Settings --------
import { loadSettings } from '../services/settingsService';

// =========== Styled Components ===========
const ScannerContainer = styled(Box)(() => ({
    width: '100%',
    maxWidth: '600px',
    margin: '0 auto',
    position: 'relative',
}));

const LiveContainer = styled(Box)(() => ({
    position: 'relative',
    display: 'none',
}));

const LiveCanvas = styled('canvas')(() => ({
    display: 'block',
    margin: '0 auto 20px auto',
    background: '#000',
    borderRadius: 8,
    imageRendering: 'pixelated',
    maxWidth: '100%',
}));

const FadeOverlay = styled(Box)(() => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'black',
    opacity: 0,
    transition: 'opacity 0.3s ease-out',
    zIndex: 20,
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const LoaderOverlay = styled(Box)(() => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'white',
    display: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 30,
}));

const CountdownDisplay = styled(Box)(() => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 100,
    height: 100,
    background: 'rgba(128, 128, 128, 0.5)',
    borderRadius: '50%',
    display: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
}));

// =========== Component ===========
export default function DocumentScanner({ onCaptureComplete }) {
    const settingsRef = useRef(loadSettings());

    /* --- Refs --- */
    const videoRef = useRef(null);
    const liveCanvasRef = useRef(null);
    const containerRef = useRef(null);
    const fadeOverlayRef = useRef(null);
    const loaderOverlayRef = useRef(null);
    const countdownDisplayRef = useRef(null);

    const scannerRef = useRef(null);   // JScanify-Instanz
    const cvReadyRef = useRef(false);  // OpenCV-Runtime geladen?
    const isMountedRef = useRef(false);

    const detectionIntervalRef = useRef(null);
    const countdownTimerRef = useRef(null);
    const countdownTriggeredRef = useRef(false);

    const liveBoundingBoxRef = useRef(null);    // für Overlay
    const frozenBoxRef = useRef(null);    // ← eingefrorene BBox (Preview)
    const frozenCornersRef = useRef(null);    // ← eingefrorene Eckpunkte (Warp)

    /* --- State --- */
    const [autoCaptured, setAutoCaptured] = useState(false);
    const [captureInProgress, setCaptureInProgress] = useState(false);
    const [detectionStableCnt, setDetectionStableCnt] = useState(0);
    const [countdownStarted, setCountdownStarted] = useState(false);
    const [countdownValue, setCountdownValue] = useState(3);

    /* --- Crop-Parameter (aus Settings) --- */
    const leftCrop = useRef(0);
    const rightCrop = useRef(0);
    const topCrop = useRef(0);
    const bottomCrop = useRef(0);
    const croppedW = useRef(0);
    const croppedH = useRef(0);

    const stabilityThreshold = 3;   // Bilder in Folge bis Auto-Capture

    // ------------------------------------------------------------------
    // OpenCV Runtime Bootstrap
    // ------------------------------------------------------------------
    useEffect(() => {

        isMountedRef.current = true;

        const onCvReady = () => {
            cvReadyRef.current = true;
            scannerRef.current = new jscanify();   // <— jscanify steht jetzt global zur Verfügung
            startCamera();
        };

        if (window.cv?.Mat) {
            // OpenCV bereits geladen
            onCvReady();
        } else {
            // warten bis WASM fertig
            window.cv = window.cv || {};
            window.cv['onRuntimeInitialized'] = onCvReady;
        }

        return () => {
            isMountedRef.current = false;
            stopCamera();
            stopDetectionInterval();
            if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ------------------------------------------------------------------
    // Camera Handling
    // ------------------------------------------------------------------
    function startCamera() {
        const cam = settingsRef.current.camera;
        const constraints = cam?.deviceId
            ? { video: { deviceId: { exact: cam.deviceId }, width: { ideal: 1920 }, height: { ideal: 1080 } } }
            : { video: { width: { ideal: 1920 }, height: { ideal: 1080 } } };

        navigator.mediaDevices.getUserMedia(constraints)
            .then(stream => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.onloadedmetadata = () => {
                        videoRef.current.play();
                        onVideoLoaded();
                    };
                }
            })
            .catch(err => console.error('[startCamera] Error:', err));
    }

    function stopCamera() {
        videoRef.current?.srcObject?.getTracks().forEach(t => t.stop());
        if (videoRef.current) videoRef.current.srcObject = null;
    }

    // ------------------------------------------------------------------
    // Video ready → Canvas konfigurieren & Scan starten
    // ------------------------------------------------------------------
    function onVideoLoaded() {
        const video = videoRef.current;
        if (!video) return;

        const vw = video.videoWidth;
        const vh = video.videoHeight;

        const { leftCropPercent, rightCropPercent, topCropPercent, bottomCropPercent } = settingsRef.current.cropping;

        leftCrop.current = leftCropPercent * vw;
        rightCrop.current = rightCropPercent * vw;
        topCrop.current = topCropPercent * vh;
        bottomCrop.current = bottomCropPercent * vh;

        croppedW.current = vw - (leftCrop.current + rightCrop.current);
        croppedH.current = vh - (topCrop.current + bottomCrop.current);

        if (liveCanvasRef.current) {
            liveCanvasRef.current.width = croppedH.current; // 90° gedreht
            liveCanvasRef.current.height = croppedW.current;
        }

        requestAnimationFrame(renderLiveView);
        startScanMode();
    }

    // ------------------------------------------------------------------
    // Canvas Live-Render (Overlay Bounding-Box)
    // ------------------------------------------------------------------
    function renderLiveView() {
        const video = videoRef.current;
        const canvas = liveCanvasRef.current;
        if (!video || !canvas) { requestAnimationFrame(renderLiveView); return; }

        const ctx = canvas.getContext('2d');
        if (!video.videoWidth) { requestAnimationFrame(renderLiveView); return; }

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.save();

        ctx.translate(canvas.width, 0);
        ctx.rotate(Math.PI / 2);

        ctx.drawImage(
            video,
            leftCrop.current, topCrop.current,
            croppedW.current, croppedH.current,
            0, 0,
            croppedW.current, croppedH.current
        );

        const bbox = liveBoundingBoxRef.current;
        if (bbox) {
            ctx.strokeStyle = '#C0FF00';
            ctx.lineWidth = 20;
            ctx.strokeRect(
                bbox.left - leftCrop.current,
                bbox.top - topCrop.current,
                bbox.width,
                bbox.height
            );
        }

        ctx.restore();
        requestAnimationFrame(renderLiveView);
    }

    // ------------------------------------------------------------------
    // Scan-Mode
    // ------------------------------------------------------------------
    function startScanMode() {
        if (containerRef.current) containerRef.current.style.display = 'block';
        setAutoCaptured(false);
        setCaptureInProgress(false);
        setDetectionStableCnt(0);
        liveBoundingBoxRef.current = null;
        frozenBoxRef.current = null;
        frozenCornersRef.current = null;
        startDetectionInterval();
    }

    function startDetectionInterval() {
        stopDetectionInterval();
        detectionIntervalRef.current = setInterval(() => {
            if (isMountedRef.current) checkDocument();
        }, 1000); // -> ggf. 300 ms, wenn Performance passt
    }

    function stopDetectionInterval() {
        if (detectionIntervalRef.current) clearInterval(detectionIntervalRef.current);
        detectionIntervalRef.current = null;
    }

    // ------------------------------------------------------------------
    // Dokumenterkennung via JScanify
    // ------------------------------------------------------------------
    async function checkDocument() {
        if (!cvReadyRef.current || !scannerRef.current) return;
        if (autoCaptured || captureInProgress || countdownStarted) return;

        const video = videoRef.current;
        if (!video?.videoWidth) return;

        // 1) Frame in Offscreen-Canvas zeichnen
        const off = document.createElement('canvas');
        off.width = video.videoWidth;
        off.height = video.videoHeight;
        off.getContext('2d').drawImage(video, 0, 0);

        // 2) Konvertiere zu OpenCV-Mat & finde Kontur
        const mat = cv.imread(off);
        const contour = scannerRef.current.findPaperContour(mat);

        // 3) Prüfen, ob Kontur gültig ist
        if (!contour || contour.size().height === 0) {
            mat.delete();
            return;
        }

        // 4) Eckpunkte extrahieren
        const corners = scannerRef.current.getCornerPoints(contour);

        // 5) Validierung: alle vier Eckpunkte müssen vorhanden sein
        if (
            !corners ||
            !corners.topLeftCorner ||
            !corners.topRightCorner ||
            !corners.bottomLeftCorner ||
            !corners.bottomRightCorner
        ) {
            contour.delete();
            mat.delete();
            return;
        }

        // 6) OpenCV-Objekte aufräumen
        contour.delete();
        mat.delete();

        // 7) Bounding-Box aus Eckpunkten berechnen
        const xs = [
            corners.topLeftCorner.x,
            corners.topRightCorner.x,
            corners.bottomLeftCorner.x,
            corners.bottomRightCorner.x,
        ];
        const ys = [
            corners.topLeftCorner.y,
            corners.topRightCorner.y,
            corners.bottomLeftCorner.y,
            corners.bottomRightCorner.y,
        ];
        const bbox = {
            left: Math.min(...xs),
            top: Math.min(...ys),
            width: Math.max(...xs) - Math.min(...xs),
            height: Math.max(...ys) - Math.min(...ys),
        };
        liveBoundingBoxRef.current = bbox;

        // 8) Stabilitäts-Logik & Countdown-Trigger
        setDetectionStableCnt(prev => {
            const next = prev + 1;
            if (next >= stabilityThreshold && !countdownStarted) {
                frozenBoxRef.current = bbox;
                frozenCornersRef.current = corners;
                stopDetectionInterval();
                startCountdown();
            }
            return next;
        });
    }


    // ------------------------------------------------------------------
    // Countdown & Cancel
    // ------------------------------------------------------------------
    function startCountdown() {
        setCountdownStarted(true);
        setCountdownValue(2);
        if (countdownDisplayRef.current) {
            countdownDisplayRef.current.style.display = 'flex';
            countdownDisplayRef.current.classList.add('running');
        }
        countdownTimerRef.current = setInterval(() => {
            setCountdownValue(v => {
                if (v <= 1) {
                    clearInterval(countdownTimerRef.current);
                    finalCapture();
                    return 0;
                }
                return v - 1;
            });
        }, 1000);
    }

    function cancelCountdown() {
        setCountdownStarted(false);
        setCountdownValue(3);
        if (countdownDisplayRef.current) {
            countdownDisplayRef.current.style.display = 'none';
            countdownDisplayRef.current.classList.remove('running');
        }
        if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    }

    // ------------------------------------------------------------------
    // Final Capture → Warp & Callback
    // ------------------------------------------------------------------
    function finalCapture() {
        if (captureInProgress || !frozenCornersRef.current) return;
        setCaptureInProgress(true);
        setAutoCaptured(true);

        fadeOverlayRef.current && (fadeOverlayRef.current.style.opacity = 1);
        countdownDisplayRef.current && (countdownDisplayRef.current.style.display = 'none');

        // leichte Verzögerung für UX-Flash
        setTimeout(() => {
            loaderOverlayRef.current && (loaderOverlayRef.current.style.display = 'flex');
        }, 300);

        // Vollbild-Frame holen
        const video = videoRef.current;
        const raw = document.createElement('canvas');
        raw.width = video.videoWidth;
        raw.height = video.videoHeight;
        raw.getContext('2d').drawImage(video, 0, 0);

        // DIN-A-Verhältnis 1:√2, hier 1000 × 1414 px
        const rawCanvas = scannerRef.current.extractPaper(
            raw,
            2100,
            1480,
            frozenCornersRef.current
        );

        loaderOverlayRef.current && (loaderOverlayRef.current.style.display = 'none');
        fadeOverlayRef.current && (fadeOverlayRef.current.style.opacity = 0);

        if (!rawCanvas) { console.error('[finalCapture] extractPaper failed'); return; }

        // *** Rotate 90° clockwise ***
        const rotated = document.createElement('canvas');
        rotated.width = rawCanvas.height;
        rotated.height = rawCanvas.width;
        const rctx = rotated.getContext('2d');
        // Move origin to center, rotate, draw
        rctx.translate(rotated.width / 2, rotated.height / 2);
        rctx.rotate(Math.PI / 2);
        rctx.drawImage(
            rawCanvas,
            -rawCanvas.width / 2,
            -rawCanvas.height / 2
        );

        // Jetzt das Data-URL von der rotierten Version
        const dataUrl = rotated.toDataURL('image/png');
        onCaptureComplete?.(dataUrl);
    }

    // ------------------------------------------------------------------
    // Render
    // ------------------------------------------------------------------
    return (
        <ScannerContainer>
            <video ref={videoRef} autoPlay playsInline style={{ display: 'none' }} />

            <LiveContainer ref={containerRef}>
                <LiveCanvas ref={liveCanvasRef} />

                <FadeOverlay ref={fadeOverlayRef}>
                    <Typography variant="h5" sx={{ color: '#fff' }}>Dokument erfasst</Typography>
                </FadeOverlay>

                <LoaderOverlay ref={loaderOverlayRef}>
                    <CircularProgress size={60} sx={{ color: '#C0FF00' }} />
                </LoaderOverlay>

                <CountdownDisplay ref={countdownDisplayRef}>
                    <Box
                        component="svg"
                        sx={{ position: 'absolute', top: 0, left: 0, width: 100, height: 100, transform: 'rotate(-90deg)', zIndex: 1 }}
                    >
                        <circle
                            cx="50" cy="50" r="45"
                            style={{
                                fill: 'none',
                                stroke: '#C0FF00',
                                strokeWidth: 4,
                                strokeDasharray: 283,
                                strokeDashoffset: countdownStarted ? 0 : 283,
                                transition: 'stroke-dashoffset 3s linear'
                            }}
                        />
                    </Box>
                    <Typography variant="h5" sx={{ position: 'relative', zIndex: 2, color: '#333' }}>
                        {countdownValue}
                    </Typography>
                </CountdownDisplay>
            </LiveContainer>
        </ScannerContainer>
    );
}
